import React, { useState, useCallback } from 'react';
import { Box, Container } from '@mui/material';

import { useScrollSpy } from '../../hooks/supplier/useScrollSpy';
import { useManualSectionPriority } from '../../hooks/supplier/use-manual-section-priority';
import { CurrentAddressSection } from '../current-address-section';
import { HeadOfficeAddressSection } from '../head-office-address-section';
import { RepresentativesSection } from '../representatives-section';
import { useSupplierRegistrationForm } from '../../hooks/supplier/use-supplier-registration-form';
import { FormActionFooter } from '../common/form-action-footer';

import { SupplierInfoSection } from './supplier-info-section';
import { FactorySection } from './factory-section';
import { CertificationsSection } from './certifications-section';
import { UnionMembershipSection } from './union-membership-section';
import { QualifiedPersonnelSection } from './qualified-personnel-section';
import { RawMaterialsSection } from './raw-materials-section';
import { TransportVehiclesSection } from './transport-vehicles-section';
import { MixerSection } from './mixer-section';
import { NavigationPanel } from './navigation-panel';

import type {
  SupplierRegistrationForm as SupplierRegistrationFormData,
  SupplierRegistrationFormProps,
  NavigationSection,
} from '../../types/company.types';

import HeaderRegister from '@/features/apps/components/header-register';
import HeaderMessage from '@/features/apps/components/header-message';

const navigationSections: NavigationSection[] = [
  { id: 'company-registration', label: 'ページTOP', anchor: 'company-registration-section' },
  { id: 'current-address', label: '所在地', anchor: 'current-address-section' },
  { id: 'head-office-address', label: '本社住所', anchor: 'head-office-address-section' },
  { id: 'nohara-representative', label: '野原担当', anchor: 'representatives-section' },
  { id: 'factory', label: '工場', anchor: 'factory-section' },
  { id: 'certifications', label: '認証', anchor: 'certifications-section' },
  { id: 'union-membership', label: '加盟組合', anchor: 'union-membership-section' },
  { id: 'qualified-personnel', label: '資格取得者', anchor: 'qualified-personnel-section' },
  { id: 'raw-materials', label: '原材料', anchor: 'raw-materials-section' },
  { id: 'mixer', label: 'ミキサ', anchor: 'mixer-section' },
  { id: 'transport-vehicles', label: '運搬車', anchor: 'transport-vehicles-section' },
];

const HEADER_OFFSET = 50;
const NAV_WIDTH = 250;
const MANUAL_SECTION_PRIORITY_MS = 1000;

export const SupplierRegistrationForm: React.FC<SupplierRegistrationFormProps> = ({
  onSubmit,
  onCancel,
}) => {
  const [activeSection, setActiveSection] = useState('company-registration');
  const { markManualSection, shouldApplySpySection } = useManualSectionPriority(
    MANUAL_SECTION_PRIORITY_MS
  );
  const [submitMessage, setSubmitMessage] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [formKey, setFormKey] = useState(0);

  const handleSuccess = useCallback(
    (response: SupplierRegistrationFormData) => {
      setSubmitMessage({ type: 'success', message: '仕入先登録が完了しました！' });
      setFormKey((prev) => prev + 1);
      if (onSubmit) onSubmit(response);
    },
    [onSubmit]
  );

  const handleError = useCallback((error: Error) => {
    setSubmitMessage({ type: 'error', message: `登録に失敗しました: ${error.message}` });
  }, []);

  const {
    formData,
    validationState,
    updateField,
    updateAddressField,
    copyCurrentToHeadOffice,
    updateFactoryAddressField,
    updateTransportVehicleField,
    updateQualifiedPersonnelField,
    updateMixerField,
    updateCertificationField,
    addRepresentative,
    updateRepresentative,
    removeRepresentative,
    addFactoryContact,
    updateFactoryContact,
    removeFactoryContact,
    updateIndustrialUnion,
    addCooperativeUnion,
    updateCooperativeUnion,
    removeCooperativeUnion,
    addRawMaterial,
    updateRawMaterial,
    removeRawMaterial,
    getFieldError,
    isFieldValid,
    getSalesPersonOptions,
    canAddRepresentative,
    isSubmitDisabled,
    handleSubmit,
    departments,
    departmentsLoading,
    staffsLoading,
    handleDepartmentChange,
  } = useSupplierRegistrationForm(handleSuccess, handleError, formKey);

  const handleFormSubmit = async () => {
    await handleSubmit();
  };

  const handleCancel = () => {
    setSubmitMessage(null);
    onCancel();
  };

  useScrollSpy(
    navigationSections,
    (id) => {
      if (!shouldApplySpySection(id)) {
        return;
      }

      if (id !== activeSection) setActiveSection(id);
    },
    { topOffset: HEADER_OFFSET, bottomOffsetRatio: 0.6 }
  );

  const handleSectionClick = (sectionId: string) => {
    markManualSection(sectionId);
    setActiveSection(sectionId);
    const section = navigationSections.find((s) => s.id === sectionId);
    if (section) {
      const el = document.getElementById(section.anchor);
      if (el) {
        const y = el.getBoundingClientRect().top + globalThis.pageYOffset - HEADER_OFFSET;
        globalThis.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  };

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      {/* Header */}
      <HeaderRegister title='仕入先登録フォーム' description='仕入先情報を入力してください' />

      <HeaderMessage
        validationState={validationState}
        submitMessage={submitMessage}
        setSubmitMessage={setSubmitMessage}
      />

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          alignItems: 'start',
          gridTemplateColumns: {
            xs: '1fr',
            md: `minmax(0,1fr) ${NAV_WIDTH}px`,
          },
        }}
      >
        {/* Form Content*/}
        <Box
          key={formKey}
          sx={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          <Box
            id='company-registration-section'
            sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
          >
            <SupplierInfoSection
              formData={formData}
              onUpdateField={(field, value) => updateField(field, value as string | number)}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
            />
          </Box>

          <Box
            id='current-address-section'
            sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
          >
            <CurrentAddressSection
              currentAddress={formData.currentAddress}
              onUpdateAddressField={(field, value) =>
                updateAddressField('currentAddress', field, value)
              }
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
            />
          </Box>

          <Box
            id='head-office-address-section'
            sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
          >
            <HeadOfficeAddressSection
              headOfficeAddress={formData.headOfficeAddress}
              onUpdateAddressField={(field, value) =>
                updateAddressField('headOfficeAddress', field, value)
              }
              onCopyCurrentToHeadOffice={copyCurrentToHeadOffice}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
            />
          </Box>

          <Box
            id='representatives-section'
            sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
          >
            <RepresentativesSection
              representatives={formData.representatives}
              onAddRepresentative={addRepresentative}
              onRemoveRepresentative={removeRepresentative}
              onUpdateRepresentative={updateRepresentative}
              getSalesPersonOptions={getSalesPersonOptions}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
              canAddRepresentative={canAddRepresentative}
              departments={departments}
              departmentsLoading={departmentsLoading}
              staffsLoading={staffsLoading}
              onDepartmentChange={handleDepartmentChange}
            />
          </Box>

          <Box id='factory-section' sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}>
            <FactorySection
              factoryAddress={formData.factoryAddress}
              factoryContacts={formData.factoryContacts}
              onUpdateFactoryAddress={updateFactoryAddressField}
              onAddFactoryContact={addFactoryContact}
              onUpdateFactoryContact={updateFactoryContact}
              onRemoveFactoryContact={removeFactoryContact}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
            />
          </Box>

          <Box
            id='certifications-section'
            sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
          >
            <CertificationsSection
              certificationInfo={formData.certificationInfo}
              onUpdateField={updateCertificationField}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
            />
          </Box>

          <Box
            id='union-membership-section'
            sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
          >
            <UnionMembershipSection
              unionMembership={formData.unionMembership}
              onUpdateIndustrialUnion={updateIndustrialUnion}
              onAddCooperativeUnion={addCooperativeUnion}
              onUpdateCooperativeUnion={updateCooperativeUnion}
              onRemoveCooperativeUnion={removeCooperativeUnion}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
            />
          </Box>

          <Box
            id='qualified-personnel-section'
            sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
          >
            <QualifiedPersonnelSection
              qualifiedPersonnel={formData.qualifiedPersonnel}
              onUpdateField={updateQualifiedPersonnelField}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
            />
          </Box>

          <Box
            id='raw-materials-section'
            sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
          >
            <RawMaterialsSection
              rawMaterials={formData.rawMaterials}
              onAddRawMaterial={addRawMaterial}
              onUpdateRawMaterial={updateRawMaterial}
              onRemoveRawMaterial={removeRawMaterial}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
            />
          </Box>

          <Box id='mixer-section' sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}>
            <MixerSection
              mixerInfo={formData.mixerInfo}
              onUpdateField={updateMixerField}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
            />
          </Box>

          <Box
            id='transport-vehicles-section'
            sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px`, pb: 2 }}
          >
            <TransportVehiclesSection
              transportVehicles={formData.transportVehicles}
              onUpdateTransportVehicleField={updateTransportVehicleField}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
            />
          </Box>

          <FormActionFooter
            isSubmitting={validationState.isSubmitting}
            isSubmitDisabled={isSubmitDisabled}
            onCancel={handleCancel}
            onSubmit={handleFormSubmit}
          />
        </Box>

        {/* Navigation Panel */}
        <Box
          sx={{
            width: { xs: '100%', md: `${NAV_WIDTH}px` },
            flexShrink: 0,
            position: { md: 'sticky' },
            top: `${HEADER_OFFSET + 12}px`,
            alignSelf: 'start',
            overflow: 'hidden',
            '& *:where(.nav-item-label, .MuiListItemText-root, .MuiTypography-root)': {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              maxWidth: '100%',
            },
          }}
        >
          <NavigationPanel
            sections={navigationSections}
            activeSection={activeSection}
            onSectionClick={handleSectionClick}
          />
        </Box>
      </Box>
    </Container>
  );
};
